import React from 'react';

/**
 * True when `icon` is an http(s) URL (a remote image) rather than an emoji or
 * text glyph. Surrounding whitespace is ignored so padded values still match.
 */
export const isIconUrl = (icon: string): boolean =>
  /^https?:\/\//i.test(icon.trim());

type ProductIconProps = {
  /** An emoji / text glyph, or an http(s) image URL. */
  icon: string;
  /** Accessible label used as the image `alt` (ignored for the emoji form). */
  name?: string;
  /** Classes applied to the `<img>` when `icon` is a URL. */
  imgClassName?: string;
  /** Classes applied to the `<span>` when `icon` is an emoji/glyph. */
  emojiClassName?: string;
  /** Rendered when `icon` is blank. Defaults to nothing. */
  fallback?: React.ReactNode;
};

/**
 * Renders a product / payment-method icon that may be either an emoji glyph or
 * a remote image URL, choosing the right element for each. Single source of
 * truth for icon rendering — it replaces the near-identical copies that used to
 * live in PaymentPage, AiPickPage and PortsPage. Sizing differs per call site,
 * so the styling is supplied via `imgClassName` / `emojiClassName`.
 */
export const ProductIcon = ({
  icon,
  name = '',
  imgClassName,
  emojiClassName,
  fallback = null,
}: ProductIconProps) => {
  const trimmed = icon.trim();
  if (!trimmed) return <>{fallback}</>;
  return isIconUrl(trimmed) ? (
    <img src={trimmed} alt={name} className={imgClassName} />
  ) : (
    <span className={emojiClassName}>{trimmed}</span>
  );
};

export default ProductIcon;
